import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { revaluation as rules } from 'further-ui/validations';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { Revaluation } from '../types';
import { HoldingClassInvestmentTotal } from '../CreateRevaluation/types';
import sumBy from 'lodash/sumBy';

type InvestmentHistory = NonNullable<
  rules.RevaluationFormType['investmentHistory']
>;
type NewRevaluations = rules.RevaluationFormType['newRevaluations'];
type AssetWithBaseCurrency = { baseCurrency?: CurrencyCode };

type UseRevaluationFormProps = {
  investmentHistory: InvestmentHistory;
  revaluations: Array<Omit<Revaluation, 'isNew'>>;
  asset: AssetWithBaseCurrency;
  newRevaluations: NewRevaluations;
  holdingClassInvestmentTotals: Array<HoldingClassInvestmentTotal>;
  initialRevaluationDate?: Date;
};

export const useRevaluationForm = ({
  investmentHistory,
  revaluations,
  asset,
  newRevaluations,
  holdingClassInvestmentTotals,
  initialRevaluationDate,
}: UseRevaluationFormProps) => {
  const initialHoldValue = sumBy(
    holdingClassInvestmentTotals,
    'currentHoldValue',
  );
  const totalUnits = sumBy(holdingClassInvestmentTotals, 'totalNumberOfUnits');
  const initialUnitPrice = totalUnits > 0 ? initialHoldValue / totalUnits : 0;

  const defaultValues: rules.RevaluationFormType = {
    revaluations: revaluations.map((rev) => ({
      ...rev,
      isNew: false as const,
    })),
    assetCurrency: asset.baseCurrency ?? CurrencyCode.GBP,
    newRevaluations,
    investmentHistory,
    currentUnitPrice: initialUnitPrice,
    currentHoldValue: initialHoldValue,
    currentRevaluationDate: initialRevaluationDate,
  };

  const formMethods = useForm({
    shouldFocusError: true,
    resolver: zodResolver(rules.RevaluationFormSchema),
    defaultValues,
    criteriaMode: 'all',
    resetOptions: {
      keepDefaultValues: false,
    },
  });

  return formMethods;
};
